import React, { useState, useEffect, useRef } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import TauriImage from "../../TauriImage/TauriImage";
import "./OpalConnectionStatus.css";
import { clearDataByStoreName, getAll, getRecordById, markAsUploadedDone } from "../../../localDB";
import config from '../../../config.json';
import appURL from '../../../appURL.json';
import OpalAlertModal from "../../OpalAlertModal/OpalAlertModal";
import { useNavigate } from "react-router-dom";
import { SyncData, SyncDone } from "../../../services/OpalSyncService/OpalSyncService";
import { useBaseProvider } from "../../../BaseManager/BaseManager";
import { LocationMap, orderStatus, SessionTag, sessionType, statusMapping, SyncStatusMap } from "../../../visualizations/visualizations";

const OpalConnectionStatus = () => {
    const { isSync, setIsSync, setTimeIn, setTimeOut } = useBaseProvider();

    const navigate = useNavigate();
    const hasInternet = navigator.onLine

    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [isOnline, setIsOnline] = useState(localStorage.getItem("isOnline") ? JSON.parse(localStorage.getItem("isOnline")) : true); // Initially online
    const [validationMessage, setValidationMessage] = useState('');
    const [showValidation, setShowValidation] = useState(false);

    const storedPermission = localStorage.getItem("permission");
    const permission = JSON.parse(atob(storedPermission));

    const dropdownRef = useRef(null);

    const getLocation = async () => {
        return new Promise((resolve, reject) => {

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    resolve({ latitude, longitude });
                },
                (error) => {
                    console.error(error);
                    reject("Unable to retrieve location");
                },
                {
                    enableHighAccuracy: true
                }
            );
        });
    };

    const updatePresenceBaseProvider = async () => {
        try {
            const todayDate = new Date();
            todayDate.setHours(0, 0, 0, 0);

            const attendance = await getAll("Attendance");
            const todayAttendance = attendance.find((a) => {
                const timeInDate = new Date(a?.TimeIn);
                timeInDate.setHours(0, 0, 0, 0); // normalize

                return timeInDate.getTime() === todayDate.getTime();
            });

            setTimeIn(todayAttendance?.TimeIn ? true : false);
            setTimeOut(todayAttendance?.TimeOut ? true : false);

        } catch (error) {
            setValidationMessage("attendance not found!");
            setShowValidation(true);
        }
    };

    // Function to toggle online/offline status
    const toggleConnection = async () => {
        if (!isOnline) {
            setIsSync(true);
            // If offline, start sync first, then set online after sync is complete
            const syncProcess = await syncProcessData();

            if (syncProcess) {
                // only continue syncing if successful
                await SyncData(true);
                await updatePresenceBaseProvider();
                setIsOnline(true);
            } else {
                setIsOnline(false);
            }

            setIsSync(false);
        } else {
            // If online, just toggle offline
            setIsOnline(false);
            setDropdownVisible(false); // Close dropdown after switching
        }
    };

    useEffect(() => {
        localStorage.setItem("isOnline", JSON.stringify(isOnline));
    }, [isOnline])

    const headerPickFields = (session) => {
        if (!session) return null;

        return {
            ID: session.ID,
            AgentID: session.AgentID,
            SessionTypeID: session.SessionTypeID,
            SessionDate: session.SessionDate,
            CyclePeriodID: session.CyclePeriodID,
            JointCallAgentID: session.JointCallAgentID,
            JointCallRemarks: session.JointCallRemarks,
            AdvancedCallReasonID: session.AdvancedCallReasonID,
            AdvancedCallRemarks: session.AdvancedCallRemarks,
            MissedCallReasonID: session.MissedCallReasonID,
            MissedCallRemarks: session.MissedCallRemarks,
            ActualStart: session.ActualStart,
            ActualEnd: session.ActualEnd,
            ActualLocationLat: session.ActualLocationLat,
            ActualLocationLong: session.ActualLocationLong,
            ActualLocation: session.ActualLocation,
            EndSessionLatitude: session.EndSessionLatitude,
            EndSessionLongitude: session.EndSessionLongitude,
            EndSessionLocation: session.EndSessionLocation,
            Duration: session.Duration,
            Status: session.Status
        };
    };

    const childPickFields = (session, tag) => {
        if (!session) return null;

        if (tag === SessionTag.pastdue) {
            return {
                SessionDate: session.SessionDate,
                Customer: { ID: session.AccountID },
                Institution: { ID: session.InstitutionID },
                ReasonID: session.MissedCallReasonID,
                Remarks: session.MissedCallRemarks
            }
        } else if (tag === SessionTag.recovered) {
            return {
                SessionDate: session.SessionDate,
                Customer: { ID: session.AccountID },
                Institution: { ID: session.InstitutionID },
                ActualStart: session.ActualStart,
                ActualEnd: session.ActualEnd,
                ActualLocationLat: session.ActualLocationLat,
                ActualLocationLong: session.ActualLocationLong,
                ActualLocation: session.ActualLocation,
                EndSessionLatitude: session.EndSessionLatitude,
                EndSessionLongitude: session.EndSessionLongitude,
                EndSessionLocation: session.EndSessionLocation,
                Duration: session.Duration
            }
        } else if (tag === SessionTag.advanced) {
            return {
                SessionDate: session.SessionDate,
                Customer: { ID: session.AccountID },
                Institution: { ID: session.InstitutionID },
                ReasonID: session.AdvancedCallReasonID,
                Remarks: session.AdvancedCallRemarks,
                ActualStart: session.ActualStart,
                ActualEnd: session.ActualEnd,
                ActualLocationLat: session.ActualLocationLat,
                ActualLocationLong: session.ActualLocationLong,
                ActualLocation: session.ActualLocation,
                EndSessionLatitude: session.EndSessionLatitude,
                EndSessionLongitude: session.EndSessionLongitude,
                EndSessionLocation: session.EndSessionLocation,
                Duration: session.Duration
            }
        } else {
            return null
        }
    };

    const sessionItemsPickFields = (item) => {
        if (!item) return null;

        return {
            ID: item.SyncStatus === SyncStatusMap.create ? null : item.ID,
            ItemID: item.ItemID,
            UnitID: item.UnitID,
            Remarks: item.Remarks,
            Executed: item.Executed,
            Lot: item.Lot,
            ExpiryDate: item.ExpiryDate,
            LocationID: item.LocationID
        };
    };

    const sessionInventoryPickFields = (item, executed, locationID) => {
        if (!item) return null;

        return {
            ID: item.SyncStatus === SyncStatusMap.create ? null : item.ID,
            ItemID: item.ItemID,
            UnitID: item.UnitID,
            Remarks: item.Remarks,
            Executed: executed,
            Lot: item.Lot,
            Expiry: item.ExpiryDate,
            Distributor: item.Distributor,
            LocationID: locationID,
            Availability: item.Availability
        };
    };

    const sessionNotesPickFields = (note) => {
        if (!note) return null;

        return {
            Content: note.Content,
            ContentType: note.ContentType,
            ReminderDate: note.ReminderDate,
            ReminderTime: note.ReminderTime,
            ReminderLocation: note.ReminderLocation,
            CreatedDate: note.CreatedDate
        };
    };

    const sessionOrderPickFields = (order, orderDetails) => {
        if (!order) return null;

        return {
            ID: order.ID?.toString(),
            OrderDate: order.OrderDate,
            AccountID: order.Account?.ID.toString(),
            Address: order.DeliveryAddress,
            Remarks: order.Remarks,
            Authorizer: order.Authorizer,
            Status: order.Status.toString(),
            Items: orderDetails
                .filter(d => d.OrderID === order.ID)
                .map(d => ({
                    ID: d.ID?.toString(),
                    ItemID: d.Item.ID?.toString(),
                    UnitID: d.Unit.ID?.toString(),
                    DetailPrice: String(d.DetailPrice ?? 0),
                    Quantity: String(d.Quantity ?? 0),
                    FreeGoods: String(d.FreeGoods ?? 0),
                    Discount: String(d.DiscountPercent ?? 0),
                    Remarks: d.Remarks || ""
                }))
        };
    };

    function formatDateForFilename(date = new Date()) {
        const pad = (num) => String(num).padStart(2, '0');
        return (
            date.getFullYear().toString() +
            pad(date.getMonth() + 1) +
            pad(date.getDate()) +
            pad(date.getHours()) +
            pad(date.getMinutes()) +
            pad(date.getSeconds())
        );
    }

    function removeNulls(obj) {
        if (Array.isArray(obj)) {
            const cleanedArray = obj.map(removeNulls).filter(v => v !== null && v !== undefined && v !== '');
            return cleanedArray.length > 0 ? cleanedArray : null;
        } else if (obj && typeof obj === 'object') {
            const cleanedEntries = Object.entries(obj)
                .map(([k, v]) => [k, removeNulls(v)])
                .filter(([_, v]) => v !== null && v !== undefined && v !== '');

            return cleanedEntries.length > 0 ? Object.fromEntries(cleanedEntries) : null;
        }
        return obj;
    }

    // Simulate syncing process
    const syncProcessData = async () => {
        const devId = localStorage.getItem("devId");
        try {
            const zip = new JSZip();
            //coverage folder
            const coverageFolder = zip.folder("coverage");
            const coverageImageFolder = coverageFolder.folder("images");
            const coverageSignatureFolder = coverageFolder.folder("signatures");
            const coverageActivitiesFolder = coverageFolder.folder("activities");

            //attendance folder
            const attendanceFolder = zip.folder("attendance");
            const attendanceImageFolder = attendanceFolder.folder("images");
            const attendanceSignatureFolder = attendanceFolder.folder("signatures");

            const addedImages = new Set();
            const addedSignatures = new Set();
            const addedInventoryImg = new Set();

            const today = new Date();
            const formattedDate = formatDateForFilename(today); // yyyyMMddhhmmss

            const allSessions = await getAll("session");
            const allSessionItems = await getAll("sessionItems");
            const allSessionNotes = await getAll("sessionNotes");
            const allAttendance = await getAll("Attendance");
            const allInventoryImages = await getAll("InvImages");
            const allSessionInventory = await getAll("SessionInventory");
            const allSessionWithdrawals = await getAll("SessionWithdrawal");
            const allSessionForRequest = await getAll("SessionForRequest");
            const allSessionOrders = await getAll("Orders");
            const allSessionOrderDetails = await getAll("OrderDetails");

            const sessionChanges = allSessions.filter(
                (a) =>
                    a.Tag == null && // only sessions with no Tag
                    (a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update) &&
                    a.Status !== statusMapping.inprogress
            );

            const rawSessionItemsChanges = allSessionItems
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update)
                .map(({ SyncStatus, ...rest }) => ({
                    ...rest,
                    ID: SyncStatus === SyncStatusMap.create ? null : rest.ID
                }));

            const sessionItemsChanges = rawSessionItemsChanges.map(removeNulls);

            const rawSessionNotesChanges = allSessionNotes
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update)
                .map(({ SyncStatus, ...rest }) => ({
                    ...rest,
                    ID: SyncStatus === SyncStatusMap.create ? null : rest.ID
                }));
            const sessionNotesChanges = rawSessionNotesChanges.map(removeNulls);

            const rawSessionInventoryChanges = allSessionInventory
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update)
                .map(({ SyncStatus, ...rest }) => ({
                    ...rest,
                    ID: SyncStatus === SyncStatusMap.create ? null : rest.ID
                }));
            const sessionInventoryChanges = rawSessionInventoryChanges.map(removeNulls);

            const rawSessionWithdrawalChanges = allSessionWithdrawals
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update)
                .map(({ SyncStatus, ...rest }) => ({
                    ...rest,
                    ID: SyncStatus === SyncStatusMap.create ? null : rest.ID
                }));
            const sessionWithdrawalChanges = rawSessionWithdrawalChanges.map(removeNulls);


            const rawSessionOrderChanges = allSessionOrders
                .filter(a =>
                    (a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update) &&
                    a.Status !== orderStatus.Cancelled
                )
                .map(({ SyncStatus, ...rest }) => ({
                    ...rest,
                    // ID: SyncStatus === SyncStatusMap.create ? null : rest.ID
                }));
            const sessionOrderChanges = rawSessionOrderChanges.map(removeNulls);

            const rawAttendanceChanges = allAttendance
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update)
                .map(({ SyncStatus, ...rest }) => ({
                    ...rest
                }));
            const attendanceChanges = rawAttendanceChanges.map(removeNulls);

            const sessionIncidental = await Promise.all(
                sessionChanges
                    .filter((a) => a.Type === sessionType.Incidental)
                    .map(async (session) => {
                        const base = headerPickFields(session);

                        const matchedCustomer = await getRecordById('customer', session.AccountID);
                        const matchedInstitution = await getRecordById('institution', session.InstitutionID);

                        const SessioninventoryImages = allInventoryImages.filter((a) => a.SessionID === session.ID)

                        if (matchedCustomer) {
                            const { SyncStatus, ID, Name, Contact_No, N_Contact_No, AddressDetail, N_AddressDetail } = matchedCustomer;

                            if (SyncStatus === SyncStatusMap.create) {
                                base.Customer = {
                                    Name,
                                    MobileNumber: Contact_No,
                                    Address: AddressDetail
                                };
                            } else if (SyncStatus === SyncStatusMap.update) {
                                base.Customer = { ID };
                                if (N_Contact_No) {
                                    base.Customer.MobileNumber = N_Contact_No;
                                }
                                if (N_AddressDetail) {
                                    base.Customer.Address = N_AddressDetail;
                                }
                            } else {
                                base.Customer = { ID };
                            }
                        } else {
                            base.Customer = null; // Optional fallback
                        }

                        if (matchedInstitution) {
                            const { SyncStatus, ID, Name, Address, N_Address, CreatedDate } = matchedInstitution;
                            if (SyncStatus === SyncStatusMap.create) {
                                base.Institution = { Name, Address, CreatedDate };
                            } else if (SyncStatus === SyncStatusMap.update) {
                                base.Institution = { ID };
                                if (N_Address) {
                                    base.Institution.Address = N_Address;
                                }
                            } else {
                                base.Institution = { ID };
                            }
                        } else {
                            base.Institution = null; // Optional fallback
                        }

                        if (session.RecipientPhotoFileID) {
                            const photoFile = await getRecordById("RecipientPhotoFile", session.RecipientPhotoFileID);
                            if (photoFile?.FileName) {
                                base.RecipientPhotoFile = photoFile.FileName;
                                if (!addedImages.has(photoFile.FileName)) {
                                    coverageImageFolder.file(photoFile.FileName, photoFile.ImageData);
                                    addedImages.add(photoFile.FileName);
                                }
                            }
                        }

                        if (session.RecipientSignatureFileID) {
                            const sigFile = await getRecordById("RecipientSignatureFile", session.RecipientSignatureFileID);
                            if (sigFile?.FileName) {
                                base.RecipientSignatureFile = sigFile.FileName;
                                if (!addedSignatures.has(sigFile.FileName)) {
                                    coverageSignatureFolder.file(sigFile.FileName, sigFile.ImageData);
                                    addedSignatures.add(sigFile.FileName);
                                }
                            }
                        }

                        for (const a of SessioninventoryImages) {
                            if (!addedInventoryImg.has(a.FileName)) {
                                const response = await fetch(a.Url);
                                const blobData = await response.blob();
                                coverageActivitiesFolder.file(a.FileName, blobData);
                                addedInventoryImg.add(a.FileName)
                            }
                        }

                        base.Samples = sessionItemsChanges
                            .filter(item => item.SessionID === session.ID)
                            .map(sessionItemsPickFields);

                        base.Inventory = sessionInventoryChanges
                            .filter(item => item.SessionID === session.ID)
                            .flatMap(item => [
                                sessionInventoryPickFields(item, item.StockRoomOnHand, LocationMap.stockRoom),
                                sessionInventoryPickFields(item, item.ShelfOnHand, LocationMap.shelf)
                            ]);

                        base.Withdrawal = sessionWithdrawalChanges
                            .filter(item => item.SessionID === session.ID)
                            .flatMap(item => [
                                sessionInventoryPickFields(item, item.StockRoomOnHand, LocationMap.stockRoom),
                                sessionInventoryPickFields(item, item.ShelfOnHand, LocationMap.shelf)
                            ]);

                        // Attach related session notes
                        base.SessionNotes = sessionNotesChanges
                            .filter(item => item.SessionID === session.ID)
                            .map(sessionNotesPickFields)

                        base.Orders = sessionOrderPickFields(
                            sessionOrderChanges.find(item => item.SessionID === session.ID),
                            allSessionOrderDetails
                        );

                        const result = { ...base, ID: null };
                        return removeNulls(result);
                    })
            );

            const enrichedSessions = await Promise.all(
                sessionChanges
                    .filter((a) => a.Type !== sessionType.Incidental)
                    .map(async (session) => {
                        const SessioninventoryImages = allInventoryImages.filter((a) => a.SessionID === session.ID)
                        const enriched = { ...headerPickFields(session) };

                        for (const a of SessioninventoryImages) {
                            if (!addedInventoryImg.has(a.FileName)) {
                                const response = await fetch(a.Url);
                                const blobData = await response.blob();
                                coverageActivitiesFolder.file(a.FileName, blobData);
                                addedInventoryImg.add(a.FileName)
                            }
                        }

                        if (session.RecipientPhotoFileID) {
                            const photoFile = await getRecordById("RecipientPhotoFile", session.RecipientPhotoFileID);
                            if (photoFile?.FileName) {
                                enriched.RecipientPhotoFile = photoFile.FileName;
                                if (!addedImages.has(photoFile.FileName)) {
                                    coverageImageFolder.file(photoFile.FileName, photoFile.ImageData);
                                    addedImages.add(photoFile.FileName);
                                }
                            }
                        }

                        if (session.RecipientSignatureFileID) {
                            const sigFile = await getRecordById("RecipientSignatureFile", session.RecipientSignatureFileID);
                            if (sigFile?.FileName) {
                                enriched.RecipientSignatureFile = sigFile.FileName;
                                if (!addedSignatures.has(sigFile.FileName)) {
                                    coverageSignatureFolder.file(sigFile.FileName, sigFile.ImageData);
                                    addedSignatures.add(sigFile.FileName);
                                }
                            }
                        }

                        enriched.Samples = sessionItemsChanges.filter(i => i.SessionID === session.ID)
                            .map(sessionItemsPickFields);

                        enriched.Inventory = sessionInventoryChanges.filter(i => i.SessionID === session.ID)
                            .flatMap(item => [
                                sessionInventoryPickFields(item, item.StockRoomOnHand, LocationMap.stockRoom),
                                sessionInventoryPickFields(item, item.ShelfOnHand, LocationMap.shelf)
                            ]);

                        enriched.Withdrawal = sessionWithdrawalChanges.filter(i => i.SessionID === session.ID)
                            .flatMap(item => [
                                sessionInventoryPickFields(item, item.StockRoomOnHand, LocationMap.stockRoom),
                                sessionInventoryPickFields(item, item.ShelfOnHand, LocationMap.shelf)
                            ]);

                        enriched.SessionNotes = sessionNotesChanges
                            .filter(i => i.SessionID === session.ID)
                            .map(sessionNotesPickFields);

                        enriched.Orders = sessionOrderPickFields(
                            sessionOrderChanges.find(i => i.SessionID === session.ID),
                            allSessionOrderDetails
                        );

                        const enrichRelatedSession = async (relatedSessionID, label, parentSessionID, tag) => {
                            if (!relatedSessionID) return;

                            enriched[label] = {
                                ID: parentSessionID,
                                ...childPickFields(session, tag),
                                RecipientSignatureFile: enriched.RecipientSignatureFile,
                                RecipientPhotoFile: enriched.RecipientPhotoFile,
                                Samples: enriched.Samples,
                                SessionNotes: enriched.SessionNotes,
                                Inventory: enriched.Inventory,
                                Withdrawal: enriched.Withdrawal
                            };
                        };

                        await enrichRelatedSession(session.AdvancedSessionID, "AdvancedSession", session.ID, SessionTag.advanced);
                        await enrichRelatedSession(session.RecoverySessionID, "RecoverySession", session.ID, SessionTag.recovered);
                        await enrichRelatedSession(session.PastDueSessionID, "PastDueSession", session.ID, SessionTag.pastdue);

                        return removeNulls(enriched);
                    })
            );

            const combinedSessions = [...enrichedSessions, ...sessionIncidental];

            const enrichedAttendance = await Promise.all(
                attendanceChanges.map(async (att) => {
                    const enriched = {
                        Date: att.Date,
                        TimeIn: {
                            Lat: att.IN_LocationLat,
                            Long: att.IN_LocationLong,
                            Timestamp: att.TimeIn,
                            PhotoFile: null,
                            SignatureFile: null,
                            Address: att.IN_Address
                        },
                        TimeOut: {
                            Lat: att.OUT_LocationLat,
                            Long: att.OUT_LocationLong,
                            Timestamp: att.TimeOut,
                            PhotoFile: null,
                            SignatureFile: null,
                            Address: att.OUT_Address
                        }
                    };

                    if (att.IN_PhotoFileID) {
                        const INPhotoFile = await getRecordById("RecipientPhotoFile", att.IN_PhotoFileID);
                        if (INPhotoFile?.FileName) {
                            enriched.TimeIn.PhotoFile = INPhotoFile.FileName;
                            if (!addedImages.has(INPhotoFile.FileName)) {
                                attendanceImageFolder.file(INPhotoFile.FileName, INPhotoFile.ImageData);
                                addedImages.add(INPhotoFile.FileName);
                            }
                        }
                    }

                    if (att.IN_SignatureFileID) {
                        const INSigFile = await getRecordById("RecipientSignatureFile", att.IN_SignatureFileID);
                        if (INSigFile?.FileName) {
                            enriched.TimeIn.SignatureFile = INSigFile.FileName;
                            if (!addedSignatures.has(INSigFile.FileName)) {
                                attendanceSignatureFolder.file(INSigFile.FileName, INSigFile.ImageData);
                                addedSignatures.add(INSigFile.FileName);
                            }
                        }
                    }

                    if (att.OUT_PhotoFileID) {
                        const OUTPhotoFile = await getRecordById("RecipientPhotoFile", att.OUT_PhotoFileID);
                        if (OUTPhotoFile?.FileName) {
                            enriched.TimeOut.PhotoFile = OUTPhotoFile.FileName;
                            if (!addedImages.has(OUTPhotoFile.FileName)) {
                                attendanceImageFolder.file(OUTPhotoFile.FileName, OUTPhotoFile.ImageData);
                                addedImages.add(OUTPhotoFile.FileName);
                            }
                        }
                    }

                    if (att.OUT_SignatureFileID) {
                        const OUTSigFile = await getRecordById("RecipientSignatureFile", att.OUT_SignatureFileID);
                        if (OUTSigFile?.FileName) {
                            enriched.TimeOut.SignatureFile = OUTSigFile.FileName;
                            if (!addedSignatures.has(OUTSigFile.FileName)) {
                                attendanceSignatureFolder.file(OUTSigFile.FileName, OUTSigFile.ImageData);
                                addedSignatures.add(OUTSigFile.FileName);
                            }
                        }
                    }

                    if (!enriched.TimeIn.Timestamp && !enriched.TimeOut.Timestamp) {
                        return null;
                    }

                    return removeNulls(enriched);
                })
            )

            // Add data.json file
            const jsonData = JSON.stringify({
                Sessions: combinedSessions,
                Attendance: removeNulls(enrichedAttendance)
            }, null, 2);
            zip.file("data.json", jsonData);

            const agntID = Number(permission?.coverage?.employee_id);
            const zipBlob = await zip.generateAsync({ type: "blob" });
            const filename = `${formattedDate}-${agntID}-${devId}.zip`;

            const result = await sendZipToApi(zipBlob, filename, formattedDate);

            if (!result.success) {
                setValidationMessage(result.message);
                setShowValidation(true);
                return false
            }

            const updateSessionItemsChanges = allSessionItems
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update);

            const updateSessionNotesChanges = allSessionNotes
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update);

            const updateSessionInventoryChanges = allSessionInventory
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update);

            const updateSessionWithdrawalChanges = allSessionWithdrawals
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update);

            const updateSessionOrderChanges = allSessionOrders
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update);

            const updateAttendanceChanges = allAttendance
                .filter(a => a.SyncStatus === SyncStatusMap.create || a.SyncStatus === SyncStatusMap.update);

            await Promise.all([
                markAsUploadedDone("session", sessionChanges),
                markAsUploadedDone("sessionItems", updateSessionItemsChanges),
                markAsUploadedDone("sessionNotes", updateSessionNotesChanges),
                markAsUploadedDone("SessionInventory", updateSessionInventoryChanges),
                markAsUploadedDone("SessionWithdrawal", updateSessionWithdrawalChanges),
                markAsUploadedDone("Orders", updateSessionOrderChanges),
                markAsUploadedDone("Attendance", updateAttendanceChanges)
            ]);

            return true;

        } catch (error) {
            setValidationMessage("Sync Failed!");
            setShowValidation(true);
            return false;
        }
    };

    const getQueryParam = (key) => {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.has(key)) {
            return searchParams.get(key);
        }
        // If not in search, check the hash
        const hash = window.location.hash;
        const [path, queryString] = hash.split("?");
        if (!queryString) return null;

        const hashParams = new URLSearchParams(queryString);
        return hashParams.get(key);
    };

    const sendZipToApi = async (zipBlob, filename, refID) => {
        const token = localStorage.getItem("token");
        const devId = localStorage.getItem("devId");

        const { latitude, longitude } = await getLocation();

        const isDownload = getQueryParam("z") === "1";

        if (isDownload) {
            saveAs(zipBlob, filename);
            return { success: true, message: "File downloaded instead of uploaded." };
        }

        const formData = new FormData();
        formData.append("Stream", zipBlob, filename);
        formData.append("ReferenceID", refID);
        formData.append("Lat", latitude);
        formData.append("Long", longitude);

        try {
            const response = await fetch(`${appURL.API_Sync}/accept`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "X-ETR-Device-ID": devId
                },
                body: formData
            });

            if (!response.ok) {
                return { success: false, message: `Sync failed: ${response.statusText}` };
            }

            return { success: response.ok };
        } catch (error) {
            return {
                success: false,
                message: error
            };
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownVisible(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        const handleSync = async () => {
            setIsSync(true);
            const isInitialDataLoaded = JSON.parse(localStorage.getItem("isInitialDataLoaded") || false);

            const syncProcess = await syncProcessData();

            if (syncProcess) {
                if (!isInitialDataLoaded) await clearDataByStoreName("Attendance");

                const result = await SyncData(!isInitialDataLoaded);
                await SyncDone();

                if (!result.success) {
                    console.error("Sync failed:", result.message);
                }

                await updatePresenceBaseProvider();
                localStorage.setItem("isInitialDataLoaded", JSON.stringify(true));
            }

            setIsSync(false);
            setIsOnline(syncProcess);
        };

        if (isOnline && hasInternet) {
            const storedInitialDataLoaded = JSON.parse(localStorage.getItem("isInitialDataLoaded") || false);
            if (!storedInitialDataLoaded) {
                handleSync();
            }
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    return (
        <div className="opal-connection-status" ref={dropdownRef}>
            <div className="dropdown p-0">
                <div
                    onClick={() => setDropdownVisible(!dropdownVisible)}
                    className="d-inline-block"
                    id="dropdownMenuButton"
                    style={{ cursor: "pointer" }}
                >
                    <TauriImage
                        localImage={isSync ? "/Images/cloud-sync.svg" : isOnline ? "/Images/cloud-online.svg" : "/Images/cloud-offline.svg"}
                        className={"nav-icon"}
                        alt="icon"
                        color={isSync ? "#FFB74D" : isOnline ? "#39C45B" : "#FF5B5B"}
                    />
                </div>

                {dropdownVisible && (
                    <div className="connection-dropdown-card">
                        <div className="d-flex align-items-start">
                            <TauriImage
                                localImage={isSync ? "/Images/cloud-sync.svg" : isOnline ? "/Images/cloud-online.svg" : "/Images/cloud-offline.svg"}
                                className={"nav-icon"}
                                alt="icon"
                                color={isSync ? "#FFB74D" : isOnline ? "#39C45B" : "#FF5B5B"}
                            />
                            <div className="status-text ms-2">
                                <div>
                                    {isSync
                                        ? "Application is synchronizing changes."
                                        : isOnline
                                            ? "You are working Online. Changes are automatically saved."
                                            : "You are currently working offline."}
                                </div>
                            </div>
                        </div>
                        <div className="text-end mt-2">
                            <button
                                className="btn btn-link p-0 switch-button"
                                onClick={toggleConnection}
                                disabled={isSync} // Disable button while syncing
                            >
                                {isSync || isOnline ? "TAKE OFFLINE" : "GO ONLINE"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <OpalAlertModal
                show={showValidation}
                onHide={() => {
                    setShowValidation(false);
                }}
                title={"Message"}
                bodyText={validationMessage}
                modalType="message"
            />
        </div>
    );
};

export default OpalConnectionStatus;
